import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Format number with locale-appropriate separators.
 */
export function formatNumber(n: number, decimals = 0): string {
  return n.toLocaleString("en-AU", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

/**
 * Format kg into tonnes (divide by 1000).
 */
export function kgToTonnes(kg: number, decimals = 1): string {
  return formatNumber(kg / 1000, decimals);
}

/**
 * Format percentage.
 */
export function formatPct(value: number, decimals = 1): string {
  return `${value.toFixed(decimals)}%`;
}

/**
 * Format a date string (ISO or YYYY-MM-DD) to AU format (d MMM yyyy).
 * e.g. "2026-02-20T00:00:00.000Z" → "20 Feb 2026"
 */
export function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString("en-AU", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

/**
 * Format a time string (HH:MM:SS) to short format (HH:MM).
 */
export function formatTime(timeStr: string | null | undefined): string {
  if (!timeStr) return "";
  // Handle both "HH:MM:SS" and full ISO timestamps
  const match = timeStr.match(/(\d{2}:\d{2})/);
  return match ? match[1] : timeStr;
}

/**
 * Get a date string N days ago from today in YYYY-MM-DD format.
 */
export function daysAgo(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().split("T")[0];
}

/**
 * Get today's date in YYYY-MM-DD format.
 */
export function today(): string {
  return new Date().toISOString().split("T")[0];
}

/**
 * Get the start of the current Australian financial year (July 1).
 * If before July, returns July 1 of the previous year.
 */
export function fyStart(): string {
  const now = new Date();
  const year = now.getMonth() >= 6 ? now.getFullYear() : now.getFullYear() - 1;
  return `${year}-07-01`;
}

/**
 * Get January 1 of the current year (year-to-date start).
 */
export function ytdStart(): string {
  return `${new Date().getFullYear()}-01-01`;
}
